import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Language, Category } from '../types';

interface Translations {
  app_name: string;
  landing_title: string;
  landing_subtitle: string;
  enter_button: string;
  sidebar_weekly_title: string;
  sidebar_weekly_text: string;
  hero_title_prefix: string;
  hero_title_highlight: string;
  hero_subtitle: string;
  search_placeholder: string;
  search_button: string;
  quick_filter: string;
  sort_by: string;
  sort_latest: string;
  sort_most_favorited: string;
  ai_recommendation: string;
  items_found: string;
  no_results: string;
  try_adjusting: string;
  clear_ai_filter: string;
  footer: string;
  categories: Record<string, string>;
  featured: string;
  ask_ai_loading: string;
  visit_site: string;
  // Admin & UI
  admin_panel_btn: string;
  admin_login_title: string;
  admin_password_placeholder: string;
  login_btn: string;
  login_error: string;
  not_admin_error: string;
  admin_email_placeholder: string;
  logging_in: string;
  resource_manager: string;
  manage_collection: string;
  fill_details: string;
  add_new_resource: string;
  edit_resource: string;
  new_btn: string;
  title_col: string;
  category_col: string;
  url_col: string;
  actions_col: string;
  no_matching_resources: string;
  title_en_label: string;
  title_zh_label: string;
  url_label: string;
  category_label: string;
  icon_label: string;
  tags_label: string;
  desc_en_label: string;
  desc_zh_label: string;
  featured_label: string;
  cancel_btn: string;
  save_resource_btn: string;
  delete_confirm: string;
  // Submission
  submit_resource: string;
  submit_resource_desc: string;
  submit_btn: string;
  submitting: string;
  submit_success: string;
  submit_error: string;
  my_submissions: string;
  pending: string;
  approved: string;
  rejected: string;
  review_note: string;
  review_submissions: string;
  review_publish_requests: string;
  my_resources: string;
  my_teams: string;
  message_center: string;
  login: string;
  logout: string;
  skip_to_content: string;
  // Auto-fill
  auto_fill_btn: string;
  auto_fill_loading: string;
  auto_fill_success: string;
  auto_fill_failed: string;
  auto_fill_invalid_url: string;
  auto_fill_rate_limited: string;
  auto_fill_wechat_unsupported: string;
  auto_fill_cloudflare_blocked: string;
}

const translations: Record<Language, Translations> = {
  en: {
    app_name: "GitScope",
    landing_title: "Knowledge, Your Way",
    landing_subtitle: "Build your private library, collaborate with your team, or share resources with the world.",
    enter_button: "Enter GitScope",
    sidebar_weekly_title: "Weekly Update",
    sidebar_weekly_text: "New AI tools added every Friday. Stay tuned!",
    hero_title_prefix: "Your knowledge,",
    hero_title_highlight: "organized",
    hero_subtitle: "From private bookmarks to team libraries to public collections — all in one place.",
    search_placeholder: "Search resources... e.g., 'React', 'Gemini'",
    search_button: "Search",
    quick_filter: "Quick filter...",
    sort_by: "Sort",
    sort_latest: "Latest",
    sort_most_favorited: "Most favorited",
    ai_recommendation: "AI Recommendation",
    items_found: "items found",
    no_results: "No results found",
    try_adjusting: "Try adjusting your search or category.",
    clear_ai_filter: "Clear Search",
    footer: "GitScope.",
    categories: {
      [Category.ALL]: "All",
      [Category.MODELS]: "Models",
      [Category.PROJECTS]: "Projects",
      [Category.PLATFORMS]: "Platforms",
      [Category.CONTENT]: "Content",
      [Category.COMMUNITY]: "Community",
      [Category.OTHER]: "Other"
    },
    featured: "Featured",
    ask_ai_loading: "Searching...",
    visit_site: "Visit",
    // Admin
    admin_panel_btn: "Admin Panel",
    admin_login_title: "Admin Login",
    admin_password_placeholder: "Enter admin password",
    login_btn: "Login",
    login_error: "Invalid credentials",
    not_admin_error: "Not authorized as admin",
    admin_email_placeholder: "Email",
    logging_in: "Logging in...",
    resource_manager: "Resource Manager",
    manage_collection: "Manage your collection",
    fill_details: "Fill in the details below",
    add_new_resource: "Add New Resource",
    edit_resource: "Edit Resource",
    new_btn: "New",
    title_col: "Title",
    category_col: "Category",
    url_col: "URL",
    actions_col: "Actions",
    no_matching_resources: "No resources found matching",
    title_en_label: "Title (English)",
    title_zh_label: "Title (Chinese)",
    url_label: "URL",
    category_label: "Category",
    icon_label: "Icon URL (Optional)",
    tags_label: "Tags (comma separated)",
    desc_en_label: "Description (English)",
    desc_zh_label: "Description (Chinese)",
    featured_label: "Mark as Featured",
    cancel_btn: "Cancel",
    save_resource_btn: "Save Resource",
    delete_confirm: "Are you sure you want to delete this resource?",
    // Submission
    submit_resource: "Submit Resource",
    submit_resource_desc: "Share a resource with the community",
    submit_btn: "Submit",
    submitting: "Submitting...",
    submit_success: "Resource submitted successfully! It will be reviewed by our team.",
    submit_error: "Failed to submit resource. Please try again.",
    my_submissions: "My Submissions",
    pending: "Pending",
    approved: "Approved",
    rejected: "Rejected",
    review_note: "Review Note",
    review_submissions: "Review Submissions",
    review_publish_requests: "Review Publish Requests",
    my_resources: "My Resources",
    my_teams: "My Teams",
    message_center: "Message Center",
    login: "Login",
    logout: "Logout",
    skip_to_content: "Skip to content",
    // Auto-fill
    auto_fill_btn: "Auto-fill",
    auto_fill_loading: "Filling...",
    auto_fill_success: "Auto-filled successfully",
    auto_fill_failed: "Auto-fill failed. Please try again.",
    auto_fill_invalid_url: "Please enter a valid URL first.",
    auto_fill_rate_limited: "Too many requests. Please try again later.",
    auto_fill_wechat_unsupported: "WeChat articles cannot be auto-filled due to anti-scraping protection. Please fill in manually.",
    auto_fill_cloudflare_blocked: "This site is protected by Cloudflare and cannot be auto-filled. Please fill in manually."
  },
  zh: {
    app_name: "GitScope",
    landing_title: "知识，随你所用",
    landing_subtitle: "构建私有知识库，与团队协作共建，向世界分享精选资源。",
    enter_button: "进入 GitScope",
    sidebar_weekly_title: "每周更新",
    sidebar_weekly_text: "每周五添加新的 AI 工具。敬请期待！",
    hero_title_prefix: "你的知识，",
    hero_title_highlight: "有序",
    hero_subtitle: "从私人收藏到团队知识库，再到公开合集——一站搞定。",
    search_placeholder: "搜索资源... 例如 “React”, “DeepSeek”",
    search_button: "搜索",
    quick_filter: "快速筛选...",
    sort_by: "排序",
    sort_latest: "最新",
    sort_most_favorited: "最多收藏",
    ai_recommendation: "AI 推荐",
    items_found: "个项目",
    no_results: "未找到结果",
    try_adjusting: "尝试调整搜索词或分类。",
    clear_ai_filter: "清除搜索",
    footer: "GitScope.",
    categories: {
      [Category.ALL]: "全部",
      [Category.MODELS]: "模型",
      [Category.PROJECTS]: "项目",
      [Category.PLATFORMS]: "平台工具",
      [Category.CONTENT]: "内容",
      [Category.COMMUNITY]: "社区",
      [Category.OTHER]: "其他"
    },
    featured: "精选",
    ask_ai_loading: "搜索中...",
    visit_site: "访问",
    // Admin
    admin_panel_btn: "管理面板",
    admin_login_title: "管理员登录",
    admin_password_placeholder: "请输入管理员密码",
    login_btn: "登录",
    login_error: "登录失败",
    not_admin_error: "无管理员权限",
    admin_email_placeholder: "邮箱",
    logging_in: "登录中...",
    resource_manager: "资源管理",
    manage_collection: "管理您的资源库",
    fill_details: "请填写以下详情",
    add_new_resource: "添加新资源",
    edit_resource: "编辑资源",
    new_btn: "新建",
    title_col: "标题",
    category_col: "分类",
    url_col: "链接",
    actions_col: "操作",
    no_matching_resources: "未找到匹配资源",
    title_en_label: "标题 (英文)",
    title_zh_label: "标题 (中文)",
    url_label: "链接",
    category_label: "分类",
    icon_label: "图标链接 (可选)",
    tags_label: "标签 (逗号分隔)",
    desc_en_label: "描述 (英文)",
    desc_zh_label: "描述 (中文)",
    featured_label: "设为精选",
    cancel_btn: "取消",
    save_resource_btn: "保存资源",
    delete_confirm: "您确定要删除此资源吗？",
    // Submission
    submit_resource: "投稿资源",
    submit_resource_desc: "与社区分享资源",
    submit_btn: "提交",
    submitting: "提交中...",
    submit_success: "资源提交成功！我们的团队将进行审核。",
    submit_error: "提交失败，请重试。",
    my_submissions: "我的投稿",
    pending: "待审核",
    approved: "已通过",
    rejected: "已拒绝",
    review_note: "审核备注",
    review_submissions: "审核投稿",
    review_publish_requests: "审核发布申请",
    my_resources: "我的资源",
    my_teams: "我的团队",
    message_center: "消息中心",
    login: "登录",
    logout: "退出登录",
    skip_to_content: "跳转到内容",
    // Auto-fill
    auto_fill_btn: "自动填充",
    auto_fill_loading: "填充中...",
    auto_fill_success: "自动填充成功",
    auto_fill_failed: "自动填充失败，请重试",
    auto_fill_invalid_url: "请先输入有效链接",
    auto_fill_rate_limited: "请求过于频繁，请稍后再试",
    auto_fill_wechat_unsupported: "微信公众号文章因反爬虫保护无法自动填充，请手动填写",
    auto_fill_cloudflare_blocked: "该网站受 Cloudflare 保护，无法自动填充，请手动填写"
  }
};

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window === 'undefined') return 'zh';
    const params = new URLSearchParams(window.location.search);
    const raw = (params.get('lang') || '').toLowerCase();
    if (raw === 'en') return 'en';
    if (raw === 'zh' || raw === 'zh-cn' || raw === 'zh_cn') return 'zh';
    return 'zh';
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    params.set('lang', lang);
    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;
    const hash = window.location.hash;
    window.history.replaceState({}, '', `${newUrl}${hash}`);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t: translations[language] }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
